/**
 * solidResolve — pure player↔solid-entity AABB resolution (Brief 009 spine extension).
 *
 * Extracted from World so it can be unit-tested headlessly (no Pixi/DOM). World calls
 * {@link resolvePlayerVsSolid} for every SOLID entity overlapping the player AFTER tile
 * collision and BEFORE the overlap pass, then applies the velocity/grounded change +
 * the entity hook (onBonk / onStand) for the returned side.
 *
 * Resolution is the standard minimum-translation-vector (MTV): push the player out
 * along the axis of LEAST penetration. Safe here because max per-step player
 * displacement (~21 px at terminal velocity) stays well under a 64 px tile, so the
 * least-overlap axis is always the true contact axis (same assumption TileGrid makes).
 *
 * Determinism: integer/float arithmetic + Math.min/abs only — ESLint-clean under
 * src/engine/ (no transcendentals, no wall-clock, no RNG).
 */
import type { AABB } from './TileGrid';

/** Which edge of the BLOCK the player resolved against. */
export type SolidSide = 'top' | 'bottom' | 'left' | 'right';

export interface SolidHit {
  /** Block edge the player was pushed off of. `bottom` = head-bonk; `top` = landed. */
  side: SolidSide;
}

function overlaps(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Push `player` out of solid `block` via MTV and report which block edge it hit, or
 * null when they do not overlap. MUTATES `player.x` / `player.y` only — the caller
 * owns the velocity / grounded / hook consequences (a SPRING needs the contact
 * velocity, a `?`-block needs the bonk signal). After this returns non-null the player
 * no longer overlaps the block on the resolved axis.
 */
export function resolvePlayerVsSolid(player: AABB, block: AABB): SolidHit | null {
  if (!overlaps(player, block)) return null;

  // Penetration depth to clear the player along each direction.
  const pushLeft = (player.x + player.w) - block.x;   // move player LEFT by this
  const pushRight = (block.x + block.w) - player.x;   // move player RIGHT by this
  const pushUp = (player.y + player.h) - block.y;     // move player UP by this (land on top)
  const pushDown = (block.y + block.h) - player.y;    // move player DOWN by this (bonk underside)

  const minX = Math.min(pushLeft, pushRight);
  const minY = Math.min(pushUp, pushDown);

  if (minX < minY) {
    if (pushLeft < pushRight) { player.x -= pushLeft; return { side: 'left' }; }
    player.x += pushRight; return { side: 'right' };
  }
  if (pushUp < pushDown) { player.y -= pushUp; return { side: 'top' }; }
  player.y += pushDown; return { side: 'bottom' };
}
